import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { differenceInDays, parseISO } from 'date-fns'
import { Phone, AlertCircle, CheckCircle, HelpCircle, ChevronRight, Minus, Plus } from 'lucide-react'
import { api } from '@/lib/api'
import { useAppStore } from '@/lib/store'
import { formatRoomPrice, localizedName } from '@/lib/format'
import { haptic } from '@/lib/telegram'
import { DateRangeCalendar } from '@/components/DateRangeCalendar'
import { parseAmenities } from '@/lib/amenities'
import type { LodgingBooking, LodgingRoomType } from '@/shared/types'

const MAX_QUANTITY = 5

interface Props {
  roomType: LodgingRoomType
  onClose: () => void
}

export function RoomBookingModal({ roomType, onClose }: Props) {
  const lang = useAppStore((s) => s.lang)
  const qc = useQueryClient()

  const { data: settings } = useQuery<{ phone_placeholder?: string }>({
    queryKey: ['settings'],
    queryFn: () => api.get('/settings'),
  })

  // 401 for guest-session users is expected — profile just stays
  // undefined and the email field below starts empty and required.
  const { data: profile } = useQuery<{ email?: string }>({
    queryKey: ['profile'],
    queryFn: () => api.get('/me'),
  })

  const [checkIn, setCheckIn] = useState('')
  const [checkOut, setCheckOut] = useState('')
  const [quantity, setQuantity] = useState(1)
  const [customerName, setCustomerName] = useState('')
  const [customerContact, setCustomerContact] = useState('')
  const [customerEmail, setCustomerEmail] = useState(profile?.email || '')
  const [customerNotes, setCustomerNotes] = useState('')
  const [earlyCheckIn, setEarlyCheckIn] = useState(false)
  const [lateCheckOut, setLateCheckOut] = useState(false)
  const [success, setSuccess] = useState<LodgingBooking[] | null>(null)
  const [submitError, setSubmitError] = useState('')

  useEffect(() => {
    if (profile?.email && !customerEmail) {
      setCustomerEmail(profile.email) // eslint-disable-line react-hooks/set-state-in-effect
    }
  }, [profile?.email, customerEmail])

  const hasRange = Boolean(checkIn && checkOut)
  const nights = hasRange ? differenceInDays(parseISO(checkOut), parseISO(checkIn)) : 0
  const minStayNights = roomType.min_stay_nights ?? 1
  const belowMinStay = nights > 0 && nights < minStayNights
  const amenities = parseAmenities(roomType.amenities)
  const photos = roomType.photos?.length ? roomType.photos : roomType.photo_url ? [roomType.photo_url] : []
  const checkInTime = roomType.check_in_time || '14:00'
  const checkOutTime = roomType.check_out_time || '11:00'

  const handleRangeChange = (nextCheckIn: string, nextCheckOut: string) => {
    setCheckIn(nextCheckIn)
    setCheckOut(nextCheckOut)
  }

  const { data: availability, isLoading: checkingAvailability } = useQuery<{
    available: boolean
    total_price_uah: number
  }>({
    queryKey: ['lodging-availability', roomType.id, checkIn, checkOut, quantity],
    queryFn: () =>
      api.post('/lodging/check-availability', {
        room_type_id: roomType.id,
        check_in_date: checkIn,
        check_out_date: checkOut,
        quantity,
      }),
    enabled: hasRange && nights > 0 && !belowMinStay,
  })

  const bookMutation = useMutation<LodgingBooking[], Error, Record<string, unknown>>({
    mutationFn: (body) => api.post('/lodging/bookings', body),
    onSuccess: (bookings) => {
      haptic('success')
      setSuccess(bookings)
      qc.invalidateQueries({ queryKey: ['lodging-my-bookings'] })
      qc.invalidateQueries({ queryKey: ['lodging-availability'] })
    },
    onError: (err) => {
      haptic('error')
      setSubmitError(err.message || t.genericError)
    },
  })

  const t = {
    book: lang === 'en' ? 'Book' : lang === 'ru' ? 'Забронировать' : 'Забронювати',
    checkIn: lang === 'en' ? 'Check-in' : lang === 'ru' ? 'Заезд' : 'Заїзд',
    checkOut: lang === 'en' ? 'Check-out' : lang === 'ru' ? 'Выезд' : 'Виїзд',
    nightsCount: (n: number) =>
      lang === 'en'
        ? `${n} ${n === 1 ? 'night' : 'nights'}`
        : lang === 'ru'
          ? `${n} ${n === 1 ? 'ночь' : n >= 2 && n <= 4 ? 'ночи' : 'ночей'}`
          : `${n} ${n === 1 ? 'ніч' : n >= 2 && n <= 4 ? 'ночі' : 'ночей'}`,
    checking: lang === 'en' ? 'Checking…' : lang === 'ru' ? 'Проверка…' : 'Перевірка…',
    available: lang === 'en' ? 'Available' : lang === 'ru' ? 'Свободен' : 'Вільний',
    booked: lang === 'en' ? 'Fully booked for these dates' : lang === 'ru' ? 'Нет свободных мест на эти даты' : 'Немає вільних місць на ці дати',
    totalPrice: lang === 'en' ? 'Total stay price' : lang === 'ru' ? 'Сумма за весь период' : 'Сума за весь період',
    quantity: lang === 'en' ? 'Number of rooms' : lang === 'ru' ? 'Количество номеров' : 'Кількість номерів',
    minStayWarning: (n: number) =>
      lang === 'en'
        ? `Minimum stay is ${n} night(s)`
        : lang === 'ru'
          ? `Минимальный срок проживания — ${n} ноч(и/ей)`
          : `Мінімальний термін проживання — ${n} ноч(і/ей)`,
    successCount: (n: number) =>
      lang === 'en'
        ? n === 1 ? '1 room booked' : `${n} rooms booked`
        : lang === 'ru'
          ? `Забронировано номеров: ${n}`
          : `Заброньовано номерів: ${n}`,
    name: lang === 'en' ? 'Your name' : lang === 'ru' ? 'Ваше имя' : "Ваше ім'я",
    contact: lang === 'en' ? 'Contact phone / Telegram' : lang === 'ru' ? 'Контактный телефон / Telegram' : 'Контактний телефон / Telegram',
    email: lang === 'en' ? 'Email' : lang === 'ru' ? 'Email' : 'Email',
    emailRequired: lang === 'en' ? 'Email is required' : lang === 'ru' ? 'Укажите email' : 'Вкажіть email',
    notes: lang === 'en' ? 'Special requests / notes' : lang === 'ru' ? 'Особые пожелания / комментарий' : 'Особливі побажання / коментар',
    offlinePayment: lang === 'en' ? 'Payment on-site / offline' : lang === 'ru' ? 'Оплата при заселении / офлайн' : 'Оплата при заселенні / офлайн',
    confirm: lang === 'en' ? 'Confirm reservation' : lang === 'ru' ? 'Подтвердить бронирование' : 'Підтвердити бронювання',
    fieldsRequired: lang === 'en' ? 'Name and contact are required' : lang === 'ru' ? 'Имя и контакты обязательны' : "Ім'я та контакти обов'язкові",
    genericError: lang === 'en' ? 'Error occurred while creating reservation' : lang === 'ru' ? 'Произошла ошибка при создании бронирования' : 'Сталася помилка під час створення бронювання',
    successTitle: lang === 'en' ? 'Reservation received!' : lang === 'ru' ? 'Бронирование успешно создано!' : 'Бронювання успішно створено!',
    successBody: lang === 'en'
      ? 'Your stay request has been registered. We will contact you shortly.'
      : lang === 'ru'
        ? 'Ваш запрос на бронирование зарегистрирован. Мы свяжемся с вами в ближайшее время.'
        : 'Ваш запит на бронювання зареєстровано. Ми зв’яжемося з вами найближчим часом.',
    close: lang === 'en' ? 'Close' : lang === 'ru' ? 'Закрыть' : 'Закрити',
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!customerName.trim() || !customerContact.trim()) {
      setSubmitError(t.fieldsRequired)
      return
    }
    if (!customerEmail.trim()) {
      setSubmitError(t.emailRequired)
      return
    }
    haptic('medium')
    bookMutation.mutate({
      room_type_id: roomType.id,
      check_in_date: checkIn,
      check_out_date: checkOut,
      customer_name: customerName,
      customer_contact: customerContact,
      customer_email: customerEmail,
      customer_notes: customerNotes,
      quantity,
      early_check_in: earlyCheckIn,
      late_check_out: lateCheckOut,
    })
  }

  const totalPrice =
    availability?.total_price_uah ?? roomType.base_price_uah * Math.max(nights, 0) * quantity
  const isAvailable = nights > 0 && !belowMinStay && availability?.available

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/80 p-4 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-md space-y-5 overflow-y-auto rounded-t-3xl border border-slate-800 bg-slate-900 p-6">
        <div className="flex items-center justify-between border-b border-slate-800 pb-2">
          <h3 className="font-bold text-lg text-slate-100">
            {localizedName(lang, roomType.name_uk, roomType.name_en, roomType.name_ru)}
          </h3>
          <button onClick={onClose} className="p-1 text-sm font-semibold text-slate-400 hover:text-slate-200">
            ✕
          </button>
        </div>

        {photos.length > 0 && (
          <div className="-mx-6 flex gap-2 overflow-x-auto px-6 pb-1">
            {photos.map((url) => (
              <img
                key={url}
                src={url}
                alt=""
                className="h-28 w-40 shrink-0 rounded-xl object-cover"
              />
            ))}
          </div>
        )}

        {amenities.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {amenities.map((a) => (
              <span
                key={a.code}
                className="flex items-center gap-1.5 rounded-full border border-slate-800 bg-slate-950 px-3 py-1 text-xs text-slate-300"
              >
                <a.icon className="h-3.5 w-3.5 text-indigo-400" />
                {a.label[lang]}
              </span>
            ))}
          </div>
        )}

        {success ? (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="mt-0.5 h-6 w-6 shrink-0 text-emerald-400" />
              <div>
                <p className="font-bold text-indigo-200">{t.successTitle}</p>
                <p className="mt-1 text-sm text-indigo-300/80">{t.successBody}</p>
              </div>
            </div>
            <div className="space-y-2 rounded-xl border border-indigo-950 bg-slate-950 p-4 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-400">{t.successCount(success.length)}</span>
                <span className="font-medium">{success[0].check_in_date} — {success[0].check_out_date}</span>
              </div>
              <div className="flex justify-between font-bold text-slate-200">
                <span>{t.totalPrice}</span>
                <span>{formatRoomPrice(success.reduce((sum, b) => sum + b.total_price_uah, 0))}</span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-full rounded-xl bg-indigo-600 py-3 font-bold text-white transition hover:bg-indigo-500"
            >
              {t.close}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <div className="mb-3 flex items-center justify-between rounded-xl border border-slate-800 bg-slate-950 p-3 text-xs">
                <div>
                  <span className="block font-semibold uppercase tracking-wider text-slate-500">
                    {lang === 'en' ? 'Check-in' : lang === 'ru' ? 'Заезд' : 'Заїзд'} ({checkInTime})
                  </span>
                  <span className="text-sm font-medium text-slate-100">{checkIn || '—'}</span>
                </div>
                <div className="text-right">
                  <span className="block font-semibold uppercase tracking-wider text-slate-500">
                    {lang === 'en' ? 'Check-out' : lang === 'ru' ? 'Отъезд' : 'Виїзд'} ({checkOutTime})
                  </span>
                  <span className="text-sm font-medium text-slate-100">{checkOut || '—'}</span>
                </div>
              </div>
              <DateRangeCalendar
                roomTypeId={roomType.id}
                checkIn={checkIn}
                checkOut={checkOut}
                quantity={quantity}
                onChange={handleRangeChange}
              />
            </div>

            <div className="flex items-center justify-between rounded-xl border border-slate-800 bg-slate-950 p-4 text-sm">
              <span className="font-semibold text-slate-300">{t.quantity}</span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  disabled={quantity <= 1}
                  className="rounded-lg border border-slate-700 p-1.5 text-slate-300 disabled:opacity-30"
                >
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span className="w-6 text-center font-bold text-slate-100">{quantity}</span>
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.min(MAX_QUANTITY, q + 1))}
                  disabled={quantity >= MAX_QUANTITY}
                  className="rounded-lg border border-slate-700 p-1.5 text-slate-300 disabled:opacity-30"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            {belowMinStay && (
              <p className="flex items-center gap-2 rounded-lg border border-amber-500/25 bg-amber-500/10 p-2 text-xs text-amber-400">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {t.minStayWarning(minStayNights)}
              </p>
            )}

            {/* Early Check-in & Late Check-out Options */}
            <div className="space-y-2 rounded-xl border border-slate-800 bg-slate-950 p-3 text-xs">
              <label className="flex items-center justify-between cursor-pointer text-slate-300">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={earlyCheckIn}
                    onChange={(e) => setEarlyCheckIn(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-700 bg-slate-900 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span>
                    {lang === 'en'
                      ? 'Early check-in (from 08:00)'
                      : lang === 'ru'
                        ? 'Ранний заезд (с 08:00)'
                        : 'Ранній заїзд (з 08:00)'}
                  </span>
                </div>
                {Boolean(roomType.early_check_in_price) && (
                  <span className="font-semibold text-indigo-400">+{formatRoomPrice(roomType.early_check_in_price || 0)}</span>
                )}
              </label>

              <label className="flex items-center justify-between cursor-pointer text-slate-300">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={lateCheckOut}
                    onChange={(e) => setLateCheckOut(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-700 bg-slate-900 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span>
                    {lang === 'en'
                      ? 'Late check-out (until 18:00)'
                      : lang === 'ru'
                        ? 'Поздний выезд (до 18:00)'
                        : 'Пізній виїзд (до 18:00)'}
                  </span>
                </div>
                {Boolean(roomType.late_check_out_price) && (
                  <span className="font-semibold text-indigo-400">+{formatRoomPrice(roomType.late_check_out_price || 0)}</span>
                )}
              </label>
            </div>

            {hasRange && !belowMinStay && (
              <div className="flex items-center justify-between rounded-xl border border-slate-800 bg-slate-950 p-4 text-sm">
                <div>
                  <span className="block text-[10px] uppercase tracking-wider text-slate-500">{t.totalPrice}</span>
                  <span className="text-xl font-extrabold text-slate-100">{formatRoomPrice(totalPrice)}</span>
                  <span className="block text-xs text-slate-500">{t.nightsCount(Math.max(nights, 0))}</span>
                </div>
                {checkingAvailability ? (
                  <span className="animate-pulse rounded-full border border-slate-800 bg-slate-950 px-2.5 py-1 text-xs text-slate-400">{t.checking}</span>
                ) : isAvailable ? (
                  <span className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-400">{t.available}</span>
                ) : (
                  <span className="rounded-full border border-rose-500/20 bg-rose-500/10 px-2.5 py-1 text-xs font-medium text-rose-400">{t.booked}</span>
                )}
              </div>
            )}

            <div>
              <label htmlFor="booking-name" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">{t.name}</label>
              <input
                id="booking-name"
                type="text"
                required
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className="w-full rounded-xl border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-slate-200 focus:border-indigo-500 focus:outline-none"
              />
            </div>

            <div>
              <label htmlFor="booking-contact" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">{t.contact}</label>
              <div className="relative">
                <Phone className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
                <input
                  id="booking-contact"
                  type="text"
                  required
                  placeholder={settings?.phone_placeholder || '+123123123'}
                  value={customerContact}
                  onChange={(e) => setCustomerContact(e.target.value)}
                  className="w-full rounded-xl border border-slate-800 bg-slate-950 py-2 pl-9 pr-3 text-sm text-slate-200 focus:border-indigo-500 focus:outline-none"
                />
              </div>
            </div>

            <div>
              <label htmlFor="booking-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">{t.email}</label>
              <input
                id="booking-email"
                type="email"
                required
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                className="w-full rounded-xl border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-slate-200 focus:border-indigo-500 focus:outline-none"
              />
            </div>

            <div>
              <label htmlFor="booking-notes" className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">{t.notes}</label>
              <textarea
                id="booking-notes"
                rows={2}
                value={customerNotes}
                onChange={(e) => setCustomerNotes(e.target.value)}
                className="w-full resize-none rounded-xl border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-slate-200 focus:border-indigo-500 focus:outline-none"
              />
            </div>

            <div className="flex items-center justify-between rounded-xl border border-slate-800 bg-slate-950 p-4 text-sm">
              <span className="flex items-center gap-1 font-semibold text-indigo-400">
                <HelpCircle className="h-3.5 w-3.5" />
                {t.offlinePayment}
              </span>
            </div>

            {submitError && (
              <p className="flex items-center gap-2 rounded-lg border border-rose-500/25 bg-rose-500/10 p-2 text-xs text-rose-400">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {submitError}
              </p>
            )}

            <button
              type="submit"
              disabled={bookMutation.isPending || !isAvailable}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 py-3 font-bold text-white shadow-lg shadow-indigo-600/25 transition hover:bg-indigo-500 disabled:bg-slate-800 disabled:text-slate-500"
            >
              {bookMutation.isPending ? (
                <div className="h-4 w-4 animate-spin rounded-full border-t-2 border-white" />
              ) : (
                <>
                  {t.confirm}
                  <ChevronRight className="h-4 w-4" />
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
